import { useEffect, useRef, useState } from 'react'

import { CONFETTI_MS, confettiPieces, drawConfetti } from '../../lib/confetti'

function prefersReducedMotion() {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * The confetti on the accepted 5th repetition (R13, `lib/confetti.js`).
 * Each new `burst` (a counter the practice bumps) plays it once over the
 * whole window — a canvas that lets every tap through — and then the canvas
 * goes. With «reduce motion» on there is no flying paper: a check mark
 * grows and fades in the middle instead, over the same short moment.
 *
 * `burst` 0 (the default) draws nothing, so the page renders exactly as
 * before until a practice that repeats is completed.
 */
export default function Confetti({ burst = 0 }) {
  const canvasRef = useRef(null)
  const [playing, setPlaying] = useState(null) // null | 'paper' | 'calm'

  useEffect(() => {
    if (!burst) return undefined
    setPlaying(prefersReducedMotion() ? 'calm' : 'paper')
    const done = setTimeout(() => setPlaying(null), CONFETTI_MS + 100)
    return () => clearTimeout(done)
  }, [burst])

  useEffect(() => {
    if (playing !== 'paper') return undefined
    const canvas = canvasRef.current
    const context = canvas?.getContext?.('2d')
    if (!context) return undefined
    const ratio = window.devicePixelRatio || 1
    const width = window.innerWidth
    const height = window.innerHeight
    canvas.width = Math.round(width * ratio)
    canvas.height = Math.round(height * ratio)
    context.scale(ratio, ratio)
    const pieces = confettiPieces(width, height)
    const start = performance.now()
    let frame = 0
    const tick = (now) => {
      const t = now - start
      drawConfetti(context, pieces, t, width, height)
      if (t < CONFETTI_MS) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [playing, burst])

  if (playing === 'calm') {
    return (
      <div aria-hidden="true" data-confetti="calm" className="pointer-events-none fixed inset-0 z-50 grid place-items-center">
        <span className="confetti-calm grid size-24 place-items-center rounded-full bg-good text-white shadow-lg">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </span>
      </div>
    )
  }
  if (playing === 'paper') {
    return <canvas ref={canvasRef} aria-hidden="true" data-confetti="paper" className="pointer-events-none fixed inset-0 z-50 h-full w-full" />
  }
  return null
}
